import PasswordStrength from '@fnando/password_strength/app/assets/javascripts/password_strength';
import { Spacing } from '@persona-id/design-tokens';
import { ArrowLeftIcon } from '@persona-id/design-tokens/icons';
import { motion } from 'framer-motion';
import { ChangeEvent, useEffect, useState } from 'react';
import { Controller, useFormState } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import Divider from 'dashboard/components/Divider';
import PasswordStrengthIndicator from 'dashboard/components/PasswordStrengthIndicator';
import ReactSelect from 'dashboard/components/ReactSelect';
import Form from 'dashboard/layouts/FormLayout/Form';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { Label } from 'design-system/components/Text';
import { DOMAIN_REGEX, NAME_REGEX } from 'lib/constants/regexes';
import { useFormSecurely } from 'lib/security/reactHookForm';

export interface FormRegisterFullProps {
  savedEmail: string;
  submit: (data: FormRegisterFullData) => void;
  setEmailStepCompleted: (emailStepCompleted: boolean) => void;
}

export const FormRegisterFull = ({
  savedEmail,
  submit,
  setEmailStepCompleted,
}: FormRegisterFullProps) => {
  const { control, handleSubmit, register, getValues } = useFormSecurely<FormRegisterFullData>();
  const { isSubmitting, errors } = useFormState({
    control,
  });

  const [passwordStrength, setPasswordStrength] = useState(null);
  const [attributionOptions, setAttributionOptions] = useState<AttributionOptionType[]>([]);

  type AttributionOptionType = {
    value: string;
    label: string;
  };

  // randomize attributionOptions once to avoid bias
  useEffect(() => {
    setAttributionOptions(
      [
        {
          value: 'event-or-conference',
          label: 'Event or conference',
        },
        {
          value: 'friend-or-colleague',
          label: 'Friend or colleague',
        },
        {
          value: 'news-or-blog',
          label: 'News or blog',
        },
        {
          value: 'social-media',
          label: 'Social media',
        },
        {
          value: 'review-site',
          label: 'Review site (e.g. G2, Capterra, SoftwareAdvice)',
        },
        {
          value: 'user-experience-another-company',
          label: 'In another company’s user experience',
        },
      ]
        .map((value) => ({ value, sort: Math.random() }))
        .sort((a, b) => a.sort - b.sort)
        .map(({ value }) => value),
    );
  }, []);

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPasswordStrength(PasswordStrength.test(savedEmail, e.target.value).score);
  };

  const isPasswordStrong = () => {
    return !!PasswordStrength.test(savedEmail, getValues('password')).isStrong();
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }}>
      <Button
        type='button'
        width='auto'
        variant='basic'
        prefixIcon={<ArrowLeftIcon />}
        onClick={() => setEmailStepCompleted(false)}
      >
        Edit email
      </Button>
      <Spacer height={2} />
      <Label htmlFor='email-address' size='lg' weight='bold'>
        Business email address
      </Label>
      <Spacer height={1} />
      <Input id='email-address' disabled placeholder={savedEmail} />
      <Spacer height={2} />
      <Label htmlFor='password' size='lg' weight='bold'>
        Create secure password
      </Label>
      <Spacer height={1} />
      <Input
        {...register('password', {
          required: 'Please select a password',
          validate: isPasswordStrong,
          onChange: (e) => {
            handlePasswordChange(e);
          },
        })}
        id='password'
        data-test='form-register__password'
        type='password'
      />
      {errors['password'] && <FormError>{errors['password'].message}</FormError>}
      {errors['password'] && errors['password']['type'] === 'validate' && (
        <FormError>
          A strong password is required. Please try a mix of letters, numbers, and symbols.
        </FormError>
      )}
      <Spacer height={1} />
      <PasswordStrengthIndicator strength={passwordStrength} />
      <Divider full />
      <Form onSubmit={handleSubmit(submit)}>
        <NameFields>
          <div>
            <Label htmlFor='name-first' size='lg' weight='bold'>
              First name
            </Label>
            <Spacer height={1} />
            <Input
              {...register('name-first', {
                required: 'Please enter your first name',
                pattern: {
                  value: NAME_REGEX,
                  message: 'Please enter a valid first name',
                },
                validate: {
                  notEmpty: (value) => {
                    if (value != null && value.trim() === '') {
                      return 'Please enter a valid first name';
                    }
                  },
                },
              })}
              id='name-first'
              data-test='form-register__name-first'
              placeholder='First name'
            />
            {errors['name-first'] && <FormError>{errors['name-first'].message}</FormError>}
          </div>
          <div>
            <Label htmlFor='name-last' size='lg' weight='bold'>
              Last name
            </Label>
            <Spacer height={1} />
            <Input
              {...register('name-last', {
                required: 'Please enter your last name',
                pattern: {
                  value: NAME_REGEX,
                  message: 'Please enter a valid last name',
                },
                validate: {
                  notEmpty: (value) => {
                    if (value != null && value.trim() === '') {
                      return 'Please enter a valid last name';
                    }
                  },
                },
              })}
              id='name-last'
              data-test='form-register__name-last'
              placeholder='Last name'
            />
            {errors['name-last'] && <FormError>{errors['name-last'].message}</FormError>}
          </div>
        </NameFields>
        <Spacer height={2} />
        <Label htmlFor='organization-name' size='lg' weight='bold'>
          Company name
        </Label>
        <Spacer height={1} />
        <Input
          {...register('organization-name', {
            required: 'Please enter your company’s name',
            pattern: {
              value: NAME_REGEX,
              message: 'Please enter a valid company name',
            },
            validate: {
              notEmpty: (value) => {
                if (value != null && value.trim() === '') {
                  return 'Please enter a valid company name';
                }
              },
            },
          })}
          id='organization-name'
          data-test='form-register__organization-name'
          placeholder='Company name'
        />
        {errors['organization-name'] && (
          <FormError>{errors['organization-name'].message}</FormError>
        )}
        <Spacer height={2} />

        <Label htmlFor='organization-domain' size='lg' weight='bold'>
          Company website
        </Label>
        <Spacer height={1} />
        <Input
          {...register('organization-domain', {
            required: 'Please enter your company’s website',
            pattern: {
              value: DOMAIN_REGEX,
              message: 'Please enter a valid website',
            },
            validate: {
              notEmpty: (value) => {
                if (value != null && value.trim() === '') {
                  return 'Please enter a valid website';
                }
              },
            },
          })}
          id='organization-domain'
          data-test='form-register__organization-domain'
          placeholder='Company website'
        />
        {errors['organization-domain'] && (
          <FormError>{errors['organization-domain'].message}</FormError>
        )}
        <Spacer height={2} />

        <Label htmlFor='organization-industry' size='lg' weight='bold'>
          Industry
        </Label>
        <Spacer height={1} />
        <Controller
          name='organization-industry'
          control={control}
          rules={{ required: 'Please select your company’s industry' }}
          render={({ field }) => (
            <Select
              id='organization-industry'
              data-test='form-register__organization-industry'
              placeholder='Select an industry...'
              {...field}
            >
              <Option value='crypto'>Crypto</Option>
              <Option value='education'>Education Technology</Option>
              <Option value='financial_services'>Fintech or Personal Finance</Option>
              <Option value='healthcare'>Healthcare</Option>
              <Option value='hr'>HR Platform</Option>
              <Option value='insurance'>Insurance</Option>
              <Option value='marketplace'>Marketplace</Option>
              <Option value='property_tech'>Property Tech</Option>
              <Option value='transportation'>Transportation</Option>
              <Option value='other'>Other</Option>
            </Select>
          )}
        />
        {errors['organization-industry'] && (
          <FormError>{errors['organization-industry'].message}</FormError>
        )}
        <Spacer height={2} />

        <Label size='lg' weight='bold'>
          How did you hear about us? (optional)
        </Label>
        <Spacer height={1} />
        <Controller
          control={control}
          name='attribution-sources'
          render={({ field: { onChange, value } }) => (
            <ReactSelect
              isMulti
              isClearable={false}
              value={value}
              onChange={onChange}
              options={attributionOptions}
              placeholder='Select an option...'
            />
          )}
        />
        {errors['attribution-sources']?.[0]?.message && (
          <FormError>{errors['attribution-sources'][0].message}</FormError>
        )}
        <Spacer height={4} />
        <Button
          data-test='form-register__submit'
          type='submit'
          disabled={isSubmitting}
          isPending={isSubmitting}
          width='auto'
        >
          Next
        </Button>
      </Form>
    </motion.div>
  );
};

const NameFields = styled('div')`
  display: flex;
  flex-direction: row;
  column-gap: ${Spacing[5]};

  > * {
    flex: 1;
  }
`;

export interface FormRegisterFullData {
  password: string;
  'name-first': string;
  'name-last': string;
  'organization-name': string;
  'organization-domain': string;
  'organization-industry': string;
  'attribution-sources': string[];
}

export default FormRegisterFull;
